/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { IAudioFactory } from "./AudioFactory";
import { IAssetUrlCache } from "types/AssetUrlCache";
import { AUDIO_EFFECTS, URL_PROVIDING_AUDIO_EFFECTS } from "./AudioEffects";
import {
  AudioEffect,
  AudioEffectClass,
  URLProvidingEffectClass,
} from "types/AudioEffect";
import { ClipId, PlaylistClip } from "types/Clip";
import { createLogger, EnhancedLogger } from "utilities/Logger";
import { UnmixedEffect } from "AudioPlayer/StreamingAudioPlayer/AudioEffects/UnmixedEffect";
import { TypedEventTarget } from "typescript-event-target";
import { Asset } from "types/Asset";

export enum StreamingClipState {
  resolvingUrl = "resolvingUrl",
  buffering = "buffering",
  ready = "ready",
  destroyed = "destroyed",
  ended = "ended",
}

interface EffectConstructors {
  urlProvidingEffect: URLProvidingEffectClass;
  otherEffects: AudioEffectClass[];
}

interface StreamingClipEvents {
  error: CustomEvent<{ clipId: ClipId }>;
  waiting: CustomEvent<{ clipId: ClipId }>;
  ready: CustomEvent<{ clipId: ClipId }>;
  ended: CustomEvent<{ clipId: ClipId }>;
}

export class StreamingClip extends TypedEventTarget<StreamingClipEvents> {
  private element?: HTMLAudioElement;
  private sourceNode?: MediaElementAudioSourceNode;

  private urlProvidingEffect: URLProvidingEffectClass = UnmixedEffect;
  private effectChain: AudioEffect[] = [];
  private clip: PlaylistClip;

  private _state: StreamingClipState = StreamingClipState.resolvingUrl;

  private readyPromise: Promise<void> | undefined = undefined;

  constructor(
    private readonly audioContext: AudioContext,
    private readonly audioFactory: IAudioFactory,
    private readonly assetUrlCache: IAssetUrlCache,
    private readonly logger: EnhancedLogger = createLogger(StreamingClip.name)
  ) {
    super();
  }

  get state(): StreamingClipState {
    return this._state;
  }

  get ready(): Promise<void> {
    return this.readyPromise ?? Promise.reject("Not initialized");
  }

  canPlay(): boolean {
    // Playing an ended clip will be a no-op but is valid.
    // We allow this because there is a bit of wiggle room with clip durations as they go through ffmpeg
    // as well as playback speed.
    // Wiggle room means that clips can finish playing while still being considered a current clip by the playlist
    // manager.
    return [StreamingClipState.ready, StreamingClipState.ended].includes(
      this.state
    );
  }

  private static effectConstructors(
    clip: PlaylistClip,
    asset: Asset
  ): EffectConstructors {
    const urlProvidingEffect = URL_PROVIDING_AUDIO_EFFECTS.find(
      (effect) =>
        effect.canBuildFor(clip) &&
        effect.providePermanentUrl(clip, asset) !== undefined
    );

    return {
      urlProvidingEffect: urlProvidingEffect ?? UnmixedEffect,
      otherEffects: AUDIO_EFFECTS.filter((effect) => effect.canBuildFor(clip)),
    };
  }

  async initialize(clip: PlaylistClip, playlistTime: number): Promise<void> {
    if (this.readyPromise) {
      throw new Error("StreamingClip already initialized");
    }

    this.readyPromise = new Promise(async (resolve) => {
      this.clip = clip;
      const asset = this.assetUrlCache.getAsset(clip.assetId);
      const { urlProvidingEffect } = StreamingClip.effectConstructors(
        clip,
        asset
      );
      this.urlProvidingEffect = urlProvidingEffect;
      this.logger.info(
        `Getting clip url for urlProvidingEffect ${urlProvidingEffect.name}`
      );
      const url = await this.assetUrlCache.getClipUrl(clip, urlProvidingEffect);

      this.logger.log("Creating audio element for", url);

      await this.createAudioElement(url, playlistTime, clip);

      resolve();
    });
    return this.readyPromise;
  }

  private async createAudioElement(
    url: string,
    playlistTime: number,
    clip: PlaylistClip
  ): Promise<void> {
    return new Promise((resolve) => {
      const element = this.audioFactory.createAudio();
      element.currentTime = this.calculateElementTime(playlistTime, clip);
      element.src = url;
      element.crossOrigin = "anonymous";
      element.preload = "auto";

      this.element = element;
      this.sourceNode = this.audioContext.createMediaElementSource(element);
      this.sourceNode.connect(this.audioContext.destination); // shouldn't need this but the test wants it

      element.oncanplay = (): void => {
        this.logger.debug("🎵 Clip ready:", {
          clip,
          mediaTime: element.currentTime,
          currentTime: new Date().toISOString(),
        });
        this._state = StreamingClipState.ready;
        this.dispatchTypedEvent(
          "ready",
          new CustomEvent("ready", {
            detail: {
              clipId: clip.id,
            },
          })
        );
        resolve();
      };

      element.onerror = (): void => {
        if (this.state === StreamingClipState.destroyed) {
          return;
        }

        this.assetUrlCache.invalidateClipUrl(clip, this.urlProvidingEffect);
        this.dispatchTypedEvent(
          "error",
          new CustomEvent("error", {
            detail: {
              clipId: clip.id,
              error: element.error,
            },
          })
        );
      };

      element.onwaiting = (): void => {
        this._state = StreamingClipState.buffering;
        this.dispatchTypedEvent(
          "waiting",
          new CustomEvent("waiting", {
            detail: {
              clipId: clip.id,
            },
          })
        );
      };

      element.onended = (): void => {
        this._state = StreamingClipState.ended;
        this.dispatchTypedEvent(
          "ended",
          new CustomEvent("ended", {
            detail: {
              clipId: clip.id,
            },
          })
        );
      };

      this._state = StreamingClipState.buffering;
    });
  }

  play(currentTime: number): void {
    if (!this.element || !this.sourceNode || !this.canPlay()) {
      throw new Error("Attempted to play uninitialized audio element");
    }

    if (this.element.ended) {
      return;
    }

    if (this.element.paused) {
      this.logger.debug(
        `▶️ Playing clip at time ${this.element.currentTime}:`,
        this.clip
      );
      this.setupEffectChain(currentTime);
      this.element.play();
    }
  }

  suspend(): void {
    if (this.element) {
      this.element.pause();
    }
  }

  resume(): void {
    if (this.element) {
      this.element.play();
    }
  }

  pause(): void {
    if (this.element) {
      this.element.pause();
      this.teardownEffectChain();
      this.sourceNode?.disconnect();
    }
  }

  stop(): void {
    this.logger.log("🛑 Stopping clip:", this.clip);
    this.pause();
    this.cleanup();
  }

  private cleanup(): void {
    if (this.element) {
      this.element.pause();
      this.element.src = "";
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
    }

    this.element = undefined;
    this.sourceNode = undefined;
    this.teardownEffectChain();
    this._state = StreamingClipState.destroyed;
  }

  private calculateElementTime(
    playlistTime: number,
    clip: PlaylistClip
  ): number {
    const clipTime = Math.max(0, playlistTime - clip.startTime);
    return (clipTime + clip.offset) / 1000; // Convert to seconds
  }

  canHotswap(newClip: PlaylistClip): boolean {
    const asset = this.assetUrlCache.getAsset(newClip.assetId);
    const { urlProvidingEffect: newUrlProvidingEffect } =
      StreamingClip.effectConstructors(newClip, asset);

    const urlProvidingEffectStable =
      newUrlProvidingEffect === this.urlProvidingEffect;

    return (
      this.element === undefined ||
      (this.element.paused && urlProvidingEffectStable) ||
      this.effectChainHotswappable(newClip)
    );
  }

  hotswap(newClip: PlaylistClip): void {
    if (!this.canHotswap(newClip)) {
      throw new Error("Invalid hotswap attempt");
    }

    this.logger.debug(`🔥 hotswap BEFORE - clip:`, this.clip);
    this.logger.debug(`🔥 hotswap BEFORE - effects:`, this.effectChain);

    this.clip = newClip;

    this.effectChain.forEach((effect) => {
      effect.hotswap(newClip);
    });

    this.logger.debug(`🔥 hotswap AFTER - clip:`, newClip);
    this.logger.debug(`🔥 hotswap AFTER - effects:`, this.effectChain);
  }

  get currentTimeMs(): number {
    return (this.element?.currentTime ?? 0) * 1000;
  }

  get isPaused(): boolean {
    return this.element?.paused ?? true;
  }

  private teardownEffectChain(): void {
    this.sourceNode?.disconnect();
    let lastEffect;
    while ((lastEffect = this.effectChain.pop())) {
      lastEffect.disconnect();
    }
  }

  private effectChainHotswappable = (newClip: PlaylistClip): boolean => {
    const asset = this.assetUrlCache.getAsset(newClip.assetId);
    const constructors = StreamingClip.effectConstructors(newClip, asset);
    const newEffects = [
      constructors.urlProvidingEffect,
      ...constructors.otherEffects,
    ];

    if (constructors.urlProvidingEffect !== this.urlProvidingEffect) {
      return false;
    }

    if (newEffects.length !== this.effectChain.length) {
      return false;
    }

    return this.effectChain.every((effect, idx) => {
      const newNodeType = newEffects[idx];
      return effect instanceof newNodeType;
    });
  };

  private setupEffectChain(currentTime: number): void {
    this.logger.debug("🚧 Setting up effect chain for clip:", this.clip);

    this.teardownEffectChain();

    let lastNode: AudioNode | undefined = undefined;

    const asset = this.assetUrlCache.getAsset(this.clip.assetId);
    const constructors = StreamingClip.effectConstructors(this.clip, asset);
    const newEffects = [
      constructors.urlProvidingEffect,
      ...constructors.otherEffects,
    ];

    for (const Effect of newEffects) {
      const effect = new Effect(
        this.audioContext,
        this.clip,
        currentTime,
        this.logger.createSubLogger(Effect.name).createSubLogger(this.clip.id)
      );

      this.logger.debug(`🔗 Adding effect to chain:`, effect);
      this.effectChain.push(effect);

      lastNode?.connect(effect.inputNode);
      lastNode = effect.outputNode;
    }

    if (this.effectChain.length > 0 && this.sourceNode) {
      this.sourceNode.connect(this.effectChain[0].inputNode);
      lastNode?.connect(this.audioContext.destination);
    }
  }
}
